// ─────────────────────────────────────────────────────────────────────────────
// campaign-result — turn a contact's per-campaign engagement signals into a
// short, plain-English RESULT label for the read-only contact modal's Activity
// tab (e.g. "Accepted — no reply", "Replied — interested", "Bounced").
//
// The read-only modal must describe WHAT HAPPENED without exposing message
// bodies or generation context — those live only in the campaign/task comms
// modal. This is the single mapping from (engagement_status, reply_category?,
// reply_sentiment, meeting_booked) → result phrase. (Ported from MADE; CARR's
// ContactCampaign carries no reply_category, so those branches simply no-op.)
// ─────────────────────────────────────────────────────────────────────────────
import { normalizeStatus, statusLabel } from "./campaign-status";

export interface CampaignResultInput {
  engagement_status: string | null | undefined;
  reply_category?: string | null;
  reply_sentiment?: string | null;
  meeting_booked?: boolean | null;
  outcome?: string | null;
}

const NEGATIVE_SENTIMENTS = new Set(["negative", "not_interested", "declined"]);

/** A short, human-readable description of where this contact landed. */
export function campaignResultLabel(input: CampaignResultInput): string {
  const status = normalizeStatus(input.engagement_status);
  const category = (input.reply_category ?? "").toLowerCase().trim();
  const sentiment = (input.reply_sentiment ?? "").toLowerCase().trim();

  if (input.meeting_booked || status === "meeting_booked") return "Meeting booked";

  // Terminal / category-driven outcomes take precedence over the funnel stage.
  if (status === "unsubscribed" || category === "unsubscribe") return "Unsubscribed";
  if (status === "bounced" || category === "bounced") return "Bounced — bad address";
  if (status === "job_changed" || category === "no_longer_at_company") {
    return "Replied — no longer at company";
  }

  const hasReplied =
    status === "replied" || status === "out_of_office" || status === "handoff_complete";

  if (hasReplied || category === "redirect" || category === "out_of_office") {
    if (status === "out_of_office" || category === "out_of_office") return "Replied — out of office";
    if (category === "redirect") return "Replied — redirected to someone else";
    if (status === "handoff_complete") return "Replied — handed to sales";
    if (NEGATIVE_SENTIMENTS.has(sentiment)) return "Replied — not interested";
    if (sentiment === "interested" || sentiment === "positive") return "Replied — interested";
    return "Replied";
  }

  switch (status) {
    case "connected":
      return "Accepted — no reply";
    case "connection_sent":
      return "Invite sent — not yet accepted";
    case "sent":
      return "Sent — no reply";
    case "planned":
    case "drafted":
    case "queued":
    case "loaded":
      return "Queued — not yet sent";
    default:
      return statusLabel(input.engagement_status);
  }
}
